<template>
  <ModalCard
    :is-responsive="true"
    :close-in-modal="true"
    :data-e2e="FinancialAdvisorEstateCypressAttribute.modal.views.ADVISOR_PRO_UPSELL_MODAL"
  >
    <template #body>
      <section
        class="p-6"
        :data-e2e="FinancialAdvisorEstateCypressAttribute.modal.views.ADVISOR_PRO_UPSELL_HERO_BAND"
      >
        <div class="columns is-multiline is-vcentered">
          <div class="column is-7 is-flex is-flex-direction-column is-gap-3">
            <img :src="LaunchRocketIcon" width="80" alt="" />
            <p class="is-size-1 has-text-weight-semibold has-text-primary">
              {{ $t('financialadvisor.pro-upsell-hero-headline') }}
            </p>
            <p class="is-size-5">
              {{ $t('financialadvisor.pro-upsell-hero-subcopy') }}
            </p>
          </div>
          <div class="column is-5">
            <FinancialAdvisorProUpsellAnnualPlanCard
              :plan="annualPlan"
              :platform-product="platformProduct"
            />
            <FinancialAdvisorProUpsellMonthlyPlanLink
              :plan="monthlyPlan"
              :platform-product="platformProduct"
            />
          </div>
        </div>
      </section>
      <section
        class="p-6 has-background-light"
        :data-e2e="
          FinancialAdvisorEstateCypressAttribute.modal.views.ADVISOR_PRO_UPSELL_WALKTHROUGH_BAND
        "
      >
        <div class="columns is-multiline is-vcentered">
          <div class="column is-7 is-flex is-flex-direction-column is-gap-3">
            <p class="has-text-primary has-text-weight-semibold is-uppercase is-size-6">
              {{ $t('financialadvisor.pro-upsell-walkthrough-title') }}
            </p>
            <p class="is-size-2 has-text-weight-semibold">
              {{ $t('financialadvisor.pro-upsell-walkthrough-description') }}
            </p>
            <p class="is-size-3 has-text-weight-medium">
              {{ $t('financialadvisor.pro-upsell-walkthrough-body') }}
            </p>
          </div>
          <div class="column is-5">
            <div
              :data-e2e="
                FinancialAdvisorEstateCypressAttribute.modal.views
                  .ADVISOR_PRO_UPSELL_WALKTHROUGH_VIDEO_SLOT
              "
            />
          </div>
        </div>
        <div
          class="columns is-multiline mt-5"
          :data-e2e="
            FinancialAdvisorEstateCypressAttribute.modal.views.ADVISOR_PRO_UPSELL_FEATURE_GRID
          "
        >
          <div
            v-for="feature in FinancialAdvisorProUpsellFeature.getValues()"
            :key="feature.key"
            class="column is-4"
          >
            <FinancialAdvisorUpsellFeatureCard
              class="feature-grid-card"
              :title="$t(feature.titleKey)"
              :description="$t(feature.descriptionKey, feature.descriptionParams)"
              :icon="feature.icon"
              :has-white-background="true"
            />
          </div>
        </div>
        <div class="is-flex is-justify-content-center mt-5">
          <ButtonView
            type="is-primary"
            :outlined="true"
            size="is-large"
            :data-e2e="
              FinancialAdvisorEstateCypressAttribute.modal.buttons.ADVISOR_PRO_UPSELL_GRID_CTA
            "
            @click="onLearnMoreAboutFeaturesClicked"
          >
            {{ $t('financialadvisor.pro-upsell-feature-grid-cta') }}
          </ButtonView>
        </div>
      </section>
    </template>
  </ModalCard>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue';
import ModalCard from '@/modules/core/ui/components/ModalCard.vue';
import { FinancialAdvisorEstateCypressAttribute } from '@/modules/financialadvisor/constants/FinancialAdvisorEstateCypressAttribute';
import LaunchRocketIcon from '@/assets/illustrations/launch-rocket.svg';
import { AdvisorPortalAdvisorProUpgradeStarted } from '@/modules/financialadvisor/models/segmentevents/AdvisorPortalAdvisorProUpgradeStarted';
import FinancialAdvisorProUpsellAnnualPlanCard from '@/modules/financialadvisor/components/stateful/FinancialAdvisorProUpsellAnnualPlanCard.vue';
import FinancialAdvisorProUpsellMonthlyPlanLink from '@/modules/financialadvisor/components/stateful/FinancialAdvisorProUpsellMonthlyPlanLink.vue';
import FinancialAdvisorUpsellFeatureCard from '@/modules/financialadvisor/components/presentational/FinancialAdvisorUpsellFeatureCard.vue';
import ButtonView from '@/modules/core/ui/components/wrappers/ButtonView.vue';
import { FinancialAdvisorProUpsellFeature } from '@/modules/financialadvisor/constants/FinancialAdvisorProUpsellFeature';
import { AdvisorPortalEntryPoint } from '@/modules/financialadvisor/constants/AdvisorPortalEntryPoint';
import { PlatformProduct } from '@/modules/platformproduct/models/PlatformProduct';
import { FinancialAdvisorProBillingPlanForm } from '@/modules/financialadvisor/models/FinancialAdvisorProBillingPlanForm';
import DocumentUIService from '@/modules/core/app/services/DocumentUIService';
import { ClearEstateLink } from '@/modules/core/app/constants/ClearEstateLink';

export default defineComponent({
  name: 'FinancialAdvisorProUpsellModalV2',
  emits: ['refer-client-clicked'],
  components: {
    ModalCard,
    FinancialAdvisorProUpsellAnnualPlanCard,
    FinancialAdvisorProUpsellMonthlyPlanLink,
    FinancialAdvisorUpsellFeatureCard,
    ButtonView
  },
  props: {
    platformProduct: {
      type: PlatformProduct as PropType<PlatformProduct>
    },
    entryPoint: {
      type: String as PropType<string>,
      default: AdvisorPortalEntryPoint.NAV
    }
  },
  static: {
    FinancialAdvisorEstateCypressAttribute,
    LaunchRocketIcon,
    FinancialAdvisorProUpsellFeature
  },
  data(): {
    annualPlan: FinancialAdvisorProBillingPlanForm;
    monthlyPlan: FinancialAdvisorProBillingPlanForm;
  } {
    return {
      annualPlan: new FinancialAdvisorProBillingPlanForm().setAsAnnualPlan(),
      monthlyPlan: new FinancialAdvisorProBillingPlanForm().setAsMonthlyPlan()
    };
  },
  methods: {
    onLearnMoreAboutFeaturesClicked(): void {
      DocumentUIService.openUrl(ClearEstateLink.ADVISOR_PRO_WEBSITE);
    }
  },
  mounted(): void {
    AdvisorPortalAdvisorProUpgradeStarted.dispatch(this.entryPoint);
  }
});
</script>

<style scoped>
.feature-grid-card {
  min-height: var(--ce-155px);
}
</style>
